import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, CreditCard, Activity, ShieldAlert, Trophy, CheckCircle2, XCircle,
  Clock, Plus, Send, Gavel, Award, Vote, UserCog, RefreshCw,
} from "lucide-react";
import { adminApi, financeApi, sportsApi, trophiesApi, disciplineApi, errMsg } from "@/lib/api";
import { PageContainer, SectionTitle, EmptyState, Loading, UserAvatar, StatusBadge, Money, SESSION_LABEL } from "@/components/shared";
import { Reveal, StaggerGroup, StaggerItem, AnimatedCounter } from "@/components/motion";
import { motion } from "framer-motion";
import { hoverLift } from "@/lib/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/* ---------------- Overview ---------------- */
const StatCard = ({ icon: Icon, label, value, color }) => (
  <StaggerItem>
    <motion.div {...hoverLift}>
      <Card><CardContent className="flex items-center gap-3 p-4">
        <div className={`rounded-full p-2.5 ${color}`}><Icon className="h-5 w-5" /></div>
        <div><AnimatedCounter value={value || 0} className="text-2xl font-heading font-bold tabular-nums" /><div className="text-xs text-muted-foreground">{label}</div></div>
      </CardContent></Card>
    </motion.div>
  </StaggerItem>
);

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const load = async () => { const [s, p] = await Promise.all([adminApi.stats(), adminApi.pending()]); setStats(s.data); setPending(p.data); };
  useEffect(() => { load(); }, []);
  const approve = async (id) => { try { await adminApi.approve(id); toast.success("Membre validé"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const reject = async (id) => { try { await adminApi.reject(id); toast.success("Demande refusée"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!stats) return <Loading />;
  return (
    <div className="space-y-6">
      <StaggerGroup className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard icon={Clock} label="En attente" value={stats.pending} color="bg-amber-100 text-amber-700" />
        <StatCard icon={Users} label="Membres actifs" value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <StatCard icon={Users} label="Membres inactifs" value={stats.inactive} color="bg-slate-100 text-slate-700" />
        <StatCard icon={ShieldAlert} label="Sanctionnés" value={stats.sanctioned} color="bg-rose-100 text-rose-700" />
        <StatCard icon={Activity} label="Sports" value={stats.sports} color="bg-sky-100 text-sky-700" />
        <StatCard icon={CreditCard} label="Cotisations actives" value={stats.collectes_active} color="bg-emerald-100 text-emerald-700" />
        <StatCard icon={Landmark2} label="Interac à valider" value={stats.pending_interac} color="bg-amber-100 text-amber-700" />
        <StatCard icon={Users} label="Total membres" value={stats.total_members} color="bg-indigo-100 text-indigo-700" />
      </StaggerGroup>
      <section className="space-y-3">
        <SectionTitle>Demandes d'inscription en attente</SectionTitle>
        {pending.length === 0 ? <EmptyState title="Aucune demande en attente" icon={CheckCircle2} /> : (
          <StaggerGroup className="space-y-3">{pending.map((m) => (
            <StaggerItem key={m.id}><Card><CardContent className="flex flex-wrap items-center gap-3 p-4">
              <UserAvatar user={m} />
              <div className="min-w-0 flex-1"><div className="font-semibold">{m.first_name} {m.last_name}</div><div className="text-xs text-muted-foreground">{m.email} · Adhésion : {m.adhesion_method === "interac" ? `Interac${m.interac_ref ? ` (${m.interac_ref})` : ""}` : "Carte"} · {m.adhesion_paid ? "Payée" : "Non payée"}</div></div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => approve(m.id)} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="admin-approuver-bouton"><CheckCircle2 className="mr-1.5 h-4 w-4" />Valider</Button>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => reject(m.id)} data-testid="admin-rejeter-bouton"><XCircle className="mr-1.5 h-4 w-4" />Refuser</Button>
              </div>
            </CardContent></Card></StaggerItem>
          ))}</StaggerGroup>
        )}
      </section>
    </div>
  );
}
// tiny inline icon alias to avoid extra import
function Landmark2(props) { return <CreditCard {...props} />; }

/* ---------------- Members ---------------- */
function MembersTab() {
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [sanctionFor, setSanctionFor] = useState(null);
  const [sanction, setSanction] = useState({ type: "avertissement", reason: "", duration_days: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { const r = await adminApi.members({ status, q }); setMembers(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [status]);

  const setRole = async (id, role) => { try { await adminApi.setRole(id, role); toast.success("Rôle mis à jour"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const applySanction = async () => {
    if (!sanction.reason) { toast.error("Motif requis"); return; }
    if (sanction.type === "suspension" && !sanction.duration_days) { toast.error("Durée requise pour suspension"); return; }
    setBusy(true);
    try {
      await disciplineApi.applySanction({ user_id: sanctionFor.id, type: sanction.type, reason: sanction.reason, duration_days: sanction.duration_days ? Number(sanction.duration_days) : null });
      toast.success("Sanction appliquée"); setSanctionFor(null); setSanction({ type: "avertissement", reason: "", duration_days: "" }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48" data-testid="admin-membres-filtre-statut"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les statuts</SelectItem><SelectItem value="pending_validation">En attente</SelectItem><SelectItem value="inactive">Inactifs</SelectItem><SelectItem value="active">Actifs</SelectItem><SelectItem value="sanctioned">Sanctionnés</SelectItem><SelectItem value="banned">Bannis</SelectItem></SelectContent></Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher…" className="w-56" data-testid="admin-membres-recherche" />
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {loading ? <Loading /> : members.length === 0 ? <EmptyState title="Aucun membre" icon={Users} /> : (
        <Card><CardContent className="p-0 overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Membre</TableHead><TableHead>Statut</TableHead><TableHead>Rôle</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{members.map((m) => (
            <TableRow key={m.id} data-testid="admin-membre-ligne">
              <TableCell><div className="flex items-center gap-2"><UserAvatar user={m} size="h-8 w-8" /><div><div className="font-medium">{m.first_name} {m.last_name}</div><div className="text-[11px] text-muted-foreground">{m.email}</div></div></div></TableCell>
              <TableCell><StatusBadge status={m.status} /></TableCell>
              <TableCell>
                <Select value={m.role} onValueChange={(v) => setRole(m.id, v)}><SelectTrigger className="h-8 w-32" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="member">Membre</SelectItem><SelectItem value="coach">Entraîneur</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1.5">
                  {m.status === "pending_validation" && <Button size="sm" className="bg-secondary hover:bg-emerald-600 text-white" onClick={async () => { await adminApi.approve(m.id); toast.success("Validé"); load(); }}>Valider</Button>}
                  {m.role !== "admin" && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setSanctionFor(m)} data-testid="admin-sanction-ouvrir"><Gavel className="mr-1 h-3.5 w-3.5" />Sanction</Button>}
                </div>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></CardContent></Card>
      )}

      <Dialog open={!!sanctionFor} onOpenChange={(o) => !o && setSanctionFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Appliquer une sanction — {sanctionFor?.first_name} {sanctionFor?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Type *</Label><Select value={sanction.type} onValueChange={(v) => setSanction({ ...sanction, type: v })}><SelectTrigger data-testid="sanctions-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="avertissement">Avertissement</SelectItem><SelectItem value="suspension">Suspension temporaire</SelectItem><SelectItem value="bannissement">Bannissement</SelectItem></SelectContent></Select></div>
            {sanction.type === "suspension" && <div className="space-y-1.5"><Label>Durée (jours) *</Label><Input type="number" value={sanction.duration_days} onChange={(e) => setSanction({ ...sanction, duration_days: e.target.value })} data-testid="sanctions-duree-input" /></div>}
            <div className="space-y-1.5"><Label>Motif *</Label><Textarea value={sanction.reason} onChange={(e) => setSanction({ ...sanction, reason: e.target.value })} data-testid="sanctions-raison-textarea" /></div>
          </div>
          <DialogFooter><Button onClick={applySanction} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="sanctions-appliquer-bouton">{busy ? "…" : "Appliquer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Collectes ---------------- */
function CollectesTab() {
  const [collectes, setCollectes] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", session: "ete", sport_id: "general", amount: "", deadline: "", reminder_days: 3 });
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { const [c, s] = await Promise.all([financeApi.collectes(), sportsApi.list()]); setCollectes(c.data); setSports(s.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.amount || !form.deadline) { toast.error("Titre, montant et échéance requis"); return; }
    setBusy(true);
    try { await financeApi.createCollecte({ title: form.title, session: form.session, sport_id: form.sport_id === "general" ? null : form.sport_id, amount: Number(form.amount), deadline: form.deadline, reminder_days: Number(form.reminder_days) }); toast.success("Cotisation créée"); setOpen(false); setForm({ title: "", session: "ete", sport_id: "general", amount: "", deadline: "", reminder_days: 3 }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = async (id) => { try { await financeApi.closeCollecte(id); toast.success("Cotisation clôturée"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remind = async (id) => { try { const r = await financeApi.remind(id); toast.success(`${r.data.reminders_sent} rappel(s) envoyé(s)`); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-4">
      <SectionTitle action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="admin-collecte-creer-bouton"><Plus className="mr-1.5 h-4 w-4" />Nouvelle cotisation</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Créer une cotisation</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="collecte-titre-input" placeholder="Cotisation Saison d'Été 2026" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Session</Label><Select value={form.session} onValueChange={(v) => setForm({ ...form, session: v })}><SelectTrigger data-testid="collecte-session-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ete">Été</SelectItem><SelectItem value="hiver">Hiver</SelectItem><SelectItem value="ponctuel">Ponctuel</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Sport</Label><Select value={form.sport_id} onValueChange={(v) => setForm({ ...form, sport_id: v })}><SelectTrigger data-testid="collecte-sport-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">Général</SelectItem>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Montant (CAD) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="collecte-montant-input" /></div>
                <div className="space-y-1.5"><Label>Échéance *</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="collecte-echeance-input" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy} data-testid="collecte-enregistrer-bouton">{busy ? "…" : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      }>Cotisations</SectionTitle>
      {loading ? <Loading /> : collectes.length === 0 ? <EmptyState title="Aucune cotisation" icon={CreditCard} /> : (
        <Card><CardContent className="p-0 overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Session</TableHead><TableHead>Montant</TableHead><TableHead>Payés</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{collectes.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">{c.title}<div className="text-[11px] text-muted-foreground">{c.sport_name}</div></TableCell>
              <TableCell>{SESSION_LABEL[c.session] || c.session}</TableCell>
              <TableCell><Money value={c.amount} /></TableCell>
              <TableCell>{c.paid_count} · <Money value={c.total_collected} /></TableCell>
              <TableCell>{c.active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="outline">Clôturée</Badge>}</TableCell>
              <TableCell className="text-right"><div className="flex justify-end gap-1.5">
                {c.active && <><Button size="sm" variant="outline" onClick={() => remind(c.id)} data-testid="collecte-relancer-bouton"><Send className="mr-1 h-3.5 w-3.5" />Relancer</Button><Button size="sm" variant="outline" className="text-rose-600" onClick={() => close(c.id)} data-testid="collecte-cloturer-bouton">Clôturer</Button></>}
              </div></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></CardContent></Card>
      )}
    </div>
  );
}

/* ---------------- Payments ---------------- */
function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [onlyInterac, setOnlyInterac] = useState(true);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const r = await financeApi.allPayments(onlyInterac); setPayments(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [onlyInterac]);
  const validate = async (id) => { try { await financeApi.validateInterac(id); toast.success("Paiement validé"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button size="sm" variant={onlyInterac ? "default" : "outline"} onClick={() => setOnlyInterac(true)} data-testid="paiements-filtre-interac">Interac à valider</Button>
        <Button size="sm" variant={!onlyInterac ? "default" : "outline"} onClick={() => setOnlyInterac(false)}>Tous les paiements</Button>
      </div>
      {loading ? <Loading /> : payments.length === 0 ? <EmptyState title="Aucun paiement" icon={CreditCard} /> : (
        <Card><CardContent className="p-0 overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Membre</TableHead><TableHead>Objet</TableHead><TableHead>Méthode</TableHead><TableHead>Réf.</TableHead><TableHead>Montant</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>{payments.map((p) => (
            <TableRow key={p.id} data-testid="paiement-ligne">
              <TableCell className="font-medium">{p.member_name}</TableCell>
              <TableCell>{p.purpose === "adhesion" ? "Adhésion" : "Cotisation"}</TableCell>
              <TableCell>{p.method === "card" ? "Carte" : "Interac"}</TableCell>
              <TableCell className="text-xs">{p.interac_reference || "—"}</TableCell>
              <TableCell><Money value={p.amount} /></TableCell>
              <TableCell>{p.payment_status === "paid" ? <Badge className="bg-emerald-100 text-emerald-800">Payé</Badge> : <Badge className="bg-amber-100 text-amber-800">En attente</Badge>}</TableCell>
              <TableCell className="text-right">{p.method === "interac" && p.payment_status === "pending" && <Button size="sm" className="bg-secondary hover:bg-emerald-600 text-white" onClick={() => validate(p.id)} data-testid="paiement-valider-bouton"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Valider</Button>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></CardContent></Card>
      )}
    </div>
  );
}

/* ---------------- Trophies ---------------- */
function TrophiesTab() {
  const [sports, setSports] = useState([]);
  const [members, setMembers] = useState([]);
  const [votes, setVotes] = useState([]);
  const [award, setAward] = useState({ type: "athlete", sport_id: "", recipient_id: "", reason: "" });
  const [monthly, setMonthly] = useState({ type: "athlete", sport_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, m, v] = await Promise.all([sportsApi.list(), adminApi.members({ status: "all" }), trophiesApi.votes()]);
    setSports(s.data); setMembers(m.data); setVotes(v.data);
    if (s.data[0]) { setAward((a) => ({ ...a, sport_id: a.sport_id || s.data[0].id })); setMonthly((mm) => ({ ...mm, sport_id: mm.sport_id || s.data[0].id })); }
  };
  useEffect(() => { load(); }, []);

  const eligible = members.filter((m) => award.type === "coach" ? (m.role === "coach" || m.role === "admin") : true).filter((m) => !award.sport_id || (m.sports || []).includes(award.sport_id) || m.role === "coach" || m.role === "admin");

  const doAward = async () => {
    if (!award.sport_id || !award.recipient_id) { toast.error("Sport et récipiendaire requis"); return; }
    setBusy(true);
    try { await trophiesApi.award(award); toast.success("Trophée attribué + notification envoyée"); setAward({ ...award, recipient_id: "", reason: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doMonthly = async () => {
    setBusy(true);
    try { const r = await trophiesApi.computeMonthly(monthly); if (r.data.result === "vote") toast.success("Égalité détectée — vote de 48h ouvert !"); else toast.success(`Vainqueur : ${r.data.recipient.first_name} ${r.data.recipient.last_name}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const closeVote = async (id) => { try { await trophiesApi.closeVote(id); toast.success("Vote clôturé, vainqueur désigné"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card><CardContent className="space-y-4 p-5">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Trophy className="h-4 w-4 text-trophy-gold" />Nomination hebdomadaire</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Type</Label><Select value={award.type} onValueChange={(v) => setAward({ ...award, type: v, recipient_id: "" })}><SelectTrigger data-testid="trophy-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="athlete">Athlète</SelectItem><SelectItem value="coach">Entraîneur</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Sport</Label><Select value={award.sport_id} onValueChange={(v) => setAward({ ...award, sport_id: v, recipient_id: "" })}><SelectTrigger data-testid="trophy-sport-select"><SelectValue /></SelectTrigger><SelectContent>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div className="space-y-1.5"><Label>Récipiendaire</Label><Select value={award.recipient_id} onValueChange={(v) => setAward({ ...award, recipient_id: v })}><SelectTrigger data-testid="trophy-recipient-select"><SelectValue placeholder="Choisir un membre" /></SelectTrigger><SelectContent>{eligible.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label>Motif (optionnel)</Label><Input value={award.reason} onChange={(e) => setAward({ ...award, reason: e.target.value })} data-testid="trophy-reason-input" /></div>
        <Button onClick={doAward} disabled={busy} className="w-full bg-secondary hover:bg-emerald-600 text-white" data-testid="trophy-attribuer-bouton"><Award className="mr-1.5 h-4 w-4" />Attribuer le trophée</Button>
      </CardContent></Card>

      <Card><CardContent className="space-y-4 p-5">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Trophy className="h-4 w-4 text-trophy-silver" />Calcul mensuel automatique</h3>
        <p className="text-sm text-muted-foreground">Détermine le vainqueur du mois (le plus de trophées hebdo). En cas d'égalité, un vote de 48h s'ouvre automatiquement.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Type</Label><Select value={monthly.type} onValueChange={(v) => setMonthly({ ...monthly, type: v })}><SelectTrigger data-testid="monthly-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="athlete">Athlète</SelectItem><SelectItem value="coach">Entraîneur</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Sport</Label><Select value={monthly.sport_id} onValueChange={(v) => setMonthly({ ...monthly, sport_id: v })}><SelectTrigger data-testid="monthly-sport-select"><SelectValue /></SelectTrigger><SelectContent>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <Button onClick={doMonthly} disabled={busy} variant="outline" className="w-full" data-testid="monthly-calculer-bouton"><Vote className="mr-1.5 h-4 w-4" />Calculer le vainqueur du mois</Button>

        <div className="border-t pt-3">
          <div className="mb-2 text-sm font-medium">Votes en cours</div>
          {votes.length === 0 ? <p className="text-sm text-muted-foreground">Aucun vote ouvert.</p> : votes.map((v) => (
            <div key={v.id} className="mb-2 flex items-center justify-between rounded-lg border p-2.5 text-sm">
              <span>{v.type === "athlete" ? "Athlète" : "Entraîneur"} du mois · {v.sport_name} · {v.total_votes} vote(s)</span>
              <Button size="sm" variant="outline" onClick={() => closeVote(v.id)} data-testid="vote-cloturer-bouton">Clôturer</Button>
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ---------------- Discipline (sanctions list) ---------------- */
function DisciplineTab() {
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const r = await disciplineApi.sanctions(); setSanctions(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const lift = async (id) => { try { await disciplineApi.liftSanction(id); toast.success("Sanction levée"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const LABEL = { avertissement: "Avertissement", suspension: "Suspension", bannissement: "Bannissement" };
  return (
    <div className="space-y-4">
      <SectionTitle>Sanctions</SectionTitle>
      {loading ? <Loading /> : sanctions.length === 0 ? <EmptyState title="Aucune sanction" icon={ShieldAlert} /> : (
        <Card><CardContent className="p-0 overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Membre</TableHead><TableHead>Type</TableHead><TableHead>Motif</TableHead><TableHead>Durée</TableHead><TableHead>État</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>{sanctions.map((s) => (
            <TableRow key={s.id}>
              <TableCell className="font-medium">{s.member_name}</TableCell>
              <TableCell>{LABEL[s.type]}</TableCell>
              <TableCell className="max-w-[240px] truncate">{s.reason}</TableCell>
              <TableCell>{s.duration_days ? `${s.duration_days} j` : "—"}</TableCell>
              <TableCell>{s.active ? <Badge className="bg-rose-100 text-rose-800">Active</Badge> : <Badge variant="outline">Levée</Badge>}</TableCell>
              <TableCell className="text-right">{s.active && <Button size="sm" variant="outline" onClick={() => lift(s.id)} data-testid="sanction-lever-bouton">Lever</Button>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></CardContent></Card>
      )}
    </div>
  );
}

export default function AdminPage() {
  return (
    <PageContainer>
      <SectionTitle>Administration</SectionTitle>
      <Reveal>
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview"><Users className="mr-1.5 h-4 w-4" />Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="members" data-testid="admin-tab-membres"><UserCog className="mr-1.5 h-4 w-4" />Membres</TabsTrigger>
          <TabsTrigger value="collectes" data-testid="admin-tab-collectes"><CreditCard className="mr-1.5 h-4 w-4" />Cotisations</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-paiements"><CreditCard className="mr-1.5 h-4 w-4" />Paiements</TabsTrigger>
          <TabsTrigger value="trophies" data-testid="admin-tab-trophies"><Trophy className="mr-1.5 h-4 w-4" />Trophées</TabsTrigger>
          <TabsTrigger value="discipline" data-testid="admin-tab-discipline"><ShieldAlert className="mr-1.5 h-4 w-4" />Discipline</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><OverviewTab /></TabsContent>
        <TabsContent value="members" className="mt-6"><MembersTab /></TabsContent>
        <TabsContent value="collectes" className="mt-6"><CollectesTab /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
        <TabsContent value="trophies" className="mt-6"><TrophiesTab /></TabsContent>
        <TabsContent value="discipline" className="mt-6"><DisciplineTab /></TabsContent>
      </Tabs>
      </Reveal>
    </PageContainer>
  );
}
